import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import OrderDetailsModal from "../components/OrderDetailsModal";
import axios from "axios";
import { format } from "date-fns";
import {
  History,
  Search,
  DollarSign,
  Truck,
  FileText,
  ArrowUpRight
} from "lucide-react";
import { showToast } from "../utils/toastUtils";

function PaymentHistory() {
  const { token } = useContext(AuthContext);
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

  // Reusable Input Style
  const inputStyle = "w-full rounded-lg border-gray-200 bg-white px-4 py-2.5 text-gray-700 placeholder-gray-400 shadow-sm transition-all duration-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none hover:border-blue-300";

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        if (token) {
          axios.defaults.headers.common["x-auth-token"] = token;
        }

        const res = await axios.get(`${BASE_URL}/api/payment-history`);
        // Sort by newest first
        const sorted = res.data.sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
        setHistory(sorted);
        setFilteredHistory(sorted);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch payment history");
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [token, BASE_URL]);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredHistory(history);
    } else {
      const lower = searchTerm.toLowerCase();
      const filtered = history.filter(record =>
        record.orderIds.some(id => id.toLowerCase().includes(lower)) ||
        record.waybillIds.some(id => id.toLowerCase().includes(lower)) ||
        (record.updatedBy && record.updatedBy.toLowerCase().includes(lower))
      );
      setFilteredHistory(filtered);
    }
  }, [searchTerm, history]);

  const handleOrderIdClick = async (orderId) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/orders`);
      const allOrders = res.data;
      const matched = allOrders.find((order) => order.orderId === orderId);
      if (matched) {
        setSelectedOrderId(matched._id);
        setIsDetailsModalOpen(true);
      } else {
        showToast("Error", `No order found for Order ID: ${orderId}`, "error");
      }
    } catch (err) {
      console.error("Failed to fetch order by orderId:", err);
    }
  };

  // KPI Calculations
  const totalCOD = filteredHistory.reduce((sum, item) => sum + (item.totalOrderAmount || 0), 0);
  const totalShipping = filteredHistory.reduce((sum, item) => sum + (item.totalShippingAmount || 0), 0);
  const totalTransactions = filteredHistory.length;

  return (
    <div className="flex bg-gray-50 min-h-screen pb-12">
      <div className="flex-1 flex flex-col w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <History className="w-8 h-8 text-blue-600" /> Payment History
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            Archive of all settled COD payments and shipping costs.
          </p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-start space-x-4 transition-all hover:shadow-md">
            <div className="p-3 rounded-lg bg-emerald-50 text-emerald-600">
              <DollarSign className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Total Settled (COD)</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">
                Rs. {totalCOD.toLocaleString("en-LK", { minimumFractionDigits: 2 })}
              </h3>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-start space-x-4 transition-all hover:shadow-md">
            <div className="p-3 rounded-lg bg-blue-50 text-blue-600">
              <Truck className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Total Shipping Paid</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">
                Rs. {totalShipping.toLocaleString("en-LK", { minimumFractionDigits: 2 })}
              </h3>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-start space-x-4 transition-all hover:shadow-md">
            <div className="p-3 rounded-lg bg-purple-50 text-purple-600">
              <FileText className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Settlements</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">
                {totalTransactions}
              </h3>
            </div>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6">
          <div className="relative group max-w-lg">
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            <input
              type="text"
              placeholder="Search by Order ID, Waybill, or User..."
              className={`${inputStyle} pl-10`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mb-4"></div>
              <p className="text-gray-500">Loading history...</p>
            </div>
          ) : error ? (
            <div className="p-6 text-center text-red-500">{error}</div>
          ) : filteredHistory.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-4">
              <div className="bg-gray-50 rounded-full p-4 mb-4">
                <Search className="w-8 h-8 text-gray-300" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">No History Found</h3>
              <p className="text-gray-500 mt-1">Try adjusting your search terms.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap w-16">#</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[200px]">Settled Orders</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[200px]">Waybills</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[140px]">Total COD (Rs)</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[140px]">Shipping (Rs)</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[150px]">Processed By</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[160px]">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {filteredHistory.map((record, index) => (
                    <tr key={record._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 align-top">
                        <div className="flex flex-wrap gap-1.5">
                          {record.orderIds.map((oid, idx) => (
                            <span
                              key={idx}
                              onClick={() => handleOrderIdClick(oid)}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 cursor-pointer border border-blue-100 transition-colors"
                              title="View Details"
                            >
                              {oid} <ArrowUpRight className="w-3 h-3 ml-1 opacity-50" />
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 align-top">
                        <div className="text-sm text-gray-600 font-mono break-words leading-relaxed">
                          {record.waybillIds.join(", ")}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-bold text-gray-900">
                        {record.totalOrderAmount.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-600">
                        {record.totalShippingAmount.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600 mr-2">
                            {(record.updatedBy || "U").charAt(0).toUpperCase()}
                          </div>
                          <span className="text-sm text-gray-900">{record.updatedBy || "Unknown"}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                        {format(new Date(record.updatedAt || record.createdAt), "MMM d, yyyy")}
                        <span className="block text-xs text-gray-400">
                          {format(new Date(record.updatedAt || record.createdAt), "hh:mm a")}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>

      <OrderDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        orderId={selectedOrderId}
        token={token}
      />
    </div>
  );
}

export default PaymentHistory;
